// SYSTEM_ROUTE: /calendar
import { useState } from 'react';
import type { EventOccurrence, RecurrenceFreq } from '@life-os/app-core';
import { RECURRENCE_FREQS, findConflicts } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function weekFrom(start: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function hhmm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

const AGENDA_LABEL: Record<string, string> = {
  scheduled_task: 'Task',
  reminder: 'Reminder',
  event: 'Event',
};

export function CalendarScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [selected, setSelected] = useState(() => isoDate(new Date()));

  // Task scheduling
  const [taskTitle, setTaskTitle] = useState('');
  const [taskTime, setTaskTime] = useState('09:00');

  // Event creation
  const [evTitle, setEvTitle] = useState('');
  const [evTime, setEvTime] = useState('10:00');
  const [evDuration, setEvDuration] = useState(60);
  const [evFreq, setEvFreq] = useState<RecurrenceFreq>('none');
  const [evInterval, setEvInterval] = useState(1);

  const week = weekFrom(new Date());
  const plan = useAsync(() => services.today.buildTodayPlan(selected), [revision, selected]);
  const occurrences = useAsync<readonly EventOccurrence[]>(
    () => services.events.occurrencesOn(selected),
    [revision, selected],
  );

  const agenda = plan.value?.agenda ?? [];
  const dayOccurrences = occurrences.value ?? [];
  const conflicts = findConflicts(dayOccurrences);
  const conflictIds = new Set(conflicts.flatMap((c) => [c.a.startsAt + c.a.title, c.b.startsAt + c.b.title]));

  const scheduleTask = async () => {
    if (taskTitle.trim().length === 0) return;
    await services.tasks.create({ title: taskTitle, lane: 'scheduled', scheduledFor: `${selected}T${taskTime}:00.000Z` });
    setTaskTitle('');
    refresh();
  };

  const createEvent = async () => {
    if (evTitle.trim().length === 0) return;
    await services.events.create({
      title: evTitle,
      startsAt: `${selected}T${evTime}:00.000Z`,
      durationMinutes: evDuration,
      recurrence: { freq: evFreq, interval: Math.max(1, evInterval), until: null },
    });
    setEvTitle('');
    refresh();
  };

  return (
    <>
      <ScreenHeader title={<>Calendar & Schedule</>} subtitle={<>Place events, tasks, and reminders in time. Conflicts stay visible.</>} />

      <Pane title="Week">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
          {week.map((d) => {
            const iso = isoDate(d);
            const active = iso === selected;
            return (
              <Button key={iso} variant={active ? 'primary' : 'ghost'} onClick={() => setSelected(iso)}>
                {d.toLocaleDateString([], { weekday: 'short', day: 'numeric' })}
              </Button>
            );
          })}
        </div>
      </Pane>

      <Pane title="New event" hint={selected}>
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={evTitle}
            placeholder="Event title…"
            onChange={(e) => setEvTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void createEvent();
            }}
            aria-label="Event title"
          />
          <input type="time" value={evTime} onChange={(e) => setEvTime(e.target.value)} aria-label="Start time" style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--lc-text-sm)', color: 'var(--lc-on-surface-variant)' }}>
            <input
              type="number"
              min={5}
              step={5}
              value={evDuration}
              onChange={(e) => setEvDuration(Math.max(5, Number(e.target.value) || 60))}
              aria-label="Duration minutes"
              style={{ ...inputStyle, width: 72 }}
            />
            min
          </label>
          <select value={evFreq} onChange={(e) => setEvFreq(e.target.value as RecurrenceFreq)} aria-label="Repeat" style={inputStyle}>
            {RECURRENCE_FREQS.map((f) => (
              <option key={f} value={f}>
                {f === 'none' ? 'does not repeat' : f}
              </option>
            ))}
          </select>
          {evFreq !== 'none' ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--lc-text-sm)', color: 'var(--lc-on-surface-variant)' }}>
              every
              <input
                type="number"
                min={1}
                value={evInterval}
                onChange={(e) => setEvInterval(Math.max(1, Number(e.target.value) || 1))}
                aria-label="Interval"
                style={{ ...inputStyle, width: 56 }}
              />
            </label>
          ) : null}
          <Button variant="primary" onClick={() => void createEvent()}>
            Add event
          </Button>
        </div>
      </Pane>

      <Pane title="Schedule a task" hint={selected}>
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: 1 }}
            value={taskTitle}
            placeholder="Task to schedule…"
            onChange={(e) => setTaskTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void scheduleTask();
            }}
            aria-label="Task to schedule"
          />
          <input type="time" value={taskTime} onChange={(e) => setTaskTime(e.target.value)} aria-label="Time" style={inputStyle} />
          <Button variant="primary" onClick={() => void scheduleTask()}>
            Schedule
          </Button>
        </div>
      </Pane>

      {conflicts.length > 0 ? (
        <Pane title="Conflicts" hint={`${conflicts.length} overlap${conflicts.length === 1 ? '' : 's'}`}>
          <Stack>
            {conflicts.map((c, i) => (
              <CompactRow
                key={i}
                title={`${c.a.title} overlaps ${c.b.title}`}
                subtitle={`${hhmm(c.a.startsAt)}–${hhmm(c.a.endsAt)} vs ${hhmm(c.b.startsAt)}–${hhmm(c.b.endsAt)}`}
                meta={<Chip tone="warn">overlap</Chip>}
              />
            ))}
          </Stack>
        </Pane>
      ) : null}

      <Pane title="Agenda" hint={selected}>
        <Stack>
          {agenda.length === 0 ? (
            <EmptyState title="Nothing scheduled">A calm day. Add an event or schedule a block above.</EmptyState>
          ) : (
            agenda.map((a) => {
              const isConflicted = a.kind === 'event' && conflictIds.has(a.at + a.title);
              return (
                <CompactRow
                  key={a.id}
                  title={a.title}
                  subtitle={`${AGENDA_LABEL[a.kind] ?? a.kind} · ${hhmm(a.at)}`}
                  meta={isConflicted ? <Chip tone="warn">conflict</Chip> : undefined}
                />
              );
            })
          )}
        </Stack>
      </Pane>
    </>
  );
}
